import { useEffect, useState, useMemo, useRef } from 'react';
import { TokenRoutes } from './TokenRoutes';
import { useTokenClient } from './useTokenClient';
import { NOT_LOGGED_IN_USER_ID } from '../constants';
import { cleanToken } from '../internals';
import { decodeToken } from '../utils/decodeToken';
import { isTokenValid } from '../utils/isTokenValid';
export function useUniversalToken({
  customerId,
  initialToken
}) {
  const userAccessTokenClient = useTokenClient(TokenRoutes.USER_ACCESS);
  const publicAccessTokenClient = useTokenClient(TokenRoutes.PUBLIC_ACCESS);
  // Create ref for token requests so they are preserved between useEffect rerun
  const userAccessTokenRequestRef = useRef();
  const publicAccessTokenRequestRef = useRef();
  const cleanInitialToken = initialToken ? cleanToken(initialToken) : undefined;
  const validInitialToken = cleanInitialToken && isTokenValid(cleanInitialToken) ? cleanInitialToken : undefined;
  const [tokenData, setTokenData] = useState({
    customerId,
    cleanToken: validInitialToken
  });
  const updateTokenData = newTokenData => {
    const newToken = newTokenData?.Token ? cleanToken(newTokenData.Token) : undefined;
    const decodedToken = newToken ? decodeToken(newToken) : null;
    setTokenData({
      customerId: decodedToken?.customer_id ? decodedToken.customer_id : NOT_LOGGED_IN_USER_ID,
      cleanToken: newToken
    });
  };

  // Attach listeners - they are executed when some token successfully changes
  useEffect(() => {
    const listener = event => updateTokenData(event.detail.tokenData);

    // We will attach on both token clients, because just one of them will work at a time
    publicAccessTokenClient.bindTokenUpdated(listener);
    userAccessTokenClient.bindTokenUpdated(listener);
    return () => {
      publicAccessTokenClient.unbindTokenUpdated(listener);
      userAccessTokenClient.unbindTokenUpdated(listener);
    };
  }, [userAccessTokenClient, publicAccessTokenClient]);

  // try to fetch new token if available on the first render (this will result in loading of the token and customerId of signed-in user)
  useEffect(() => {
    const fetchToken = async () => {
      try {
        userAccessTokenRequestRef.current ??= userAccessTokenClient.getToken();
        updateTokenData(await userAccessTokenRequestRef.current);
      } catch (error) {
        publicAccessTokenRequestRef.current ??= publicAccessTokenClient.getToken();
        updateTokenData(await publicAccessTokenRequestRef.current);
      }
    };
    fetchToken().catch(error => {
      console.log(`Some error occurred while getting token: ${error}`);
      setTokenData(null);
    });
  }, [userAccessTokenRequestRef, publicAccessTokenRequestRef, publicAccessTokenClient, userAccessTokenClient]);
  return useMemo(() => ({
    token: tokenData?.cleanToken ?? '',
    // TODO (JN): This should be nullable to reflect the real state of the app. Consumers then may decide what to do when token is not available. But it is going to be a breaking change.
    customerId: tokenData?.customerId ?? NOT_LOGGED_IN_USER_ID,
    formattedToken: tokenData?.cleanToken ? `Bearer ${tokenData.cleanToken}` : '',
    isAnonymousUser: tokenData?.customerId === NOT_LOGGED_IN_USER_ID
  }), [tokenData?.customerId, tokenData?.cleanToken]);
}
//# sourceMappingURL=useUniversalToken.js.map